import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, MapPin, ShieldCheck } from 'lucide-react';
import { SEO, ORGANIZATION_LD, LOCAL_BUSINESS_LD, SITE_URL } from '../components/SEO';

const HOME_JSONLD = {
  '@context': 'https://schema.org',
  '@graph': [
    ORGANIZATION_LD,
    LOCAL_BUSINESS_LD,
    {
      '@type': 'Service',
      name: 'Strategy Session',
      provider: { '@id': `${SITE_URL}/#localbusiness` },
      areaServed: { '@type': 'City', name: 'Nashville' },
      description: 'A deep-dive consultation for budget insight, vendor direction, and early-stage risk identification.',
      offers: { '@type': 'Offer', price: '300', priceCurrency: 'USD', url: `${SITE_URL}/contact?services=Strategy+Session+%28%24300%29` },
    },
    {
      '@type': 'Service',
      name: 'Concierge Advisory',
      provider: { '@id': `${SITE_URL}/#localbusiness` },
      areaServed: { '@type': 'City', name: 'Nashville' },
      description: 'Structured advisory support: line-by-line budget architecture, vendor shortlist, and contract guidance.',
      offers: { '@type': 'Offer', price: '1800', priceCurrency: 'USD', url: `${SITE_URL}/consulting` },
    },
    {
      '@type': 'Service',
      name: 'Full Concierge Oversight',
      provider: { '@id': `${SITE_URL}/#localbusiness` },
      areaServed: { '@type': 'City', name: 'Nashville' },
      description: 'Continuous oversight from start to execution: vendor coordination, contract review, budget monitoring.',
      offers: { '@type': 'Offer', price: '3500', priceCurrency: 'USD', url: `${SITE_URL}/consulting` },
    },
    {
      '@type': 'Service',
      name: 'Elite Concierge',
      provider: { '@id': `${SITE_URL}/#localbusiness` },
      areaServed: { '@type': 'City', name: 'Nashville' },
      description: 'Bespoke advisory engagement for complex, high-investment events.',
      offers: { '@type': 'Offer', priceCurrency: 'USD', url: `${SITE_URL}/contact?services=Elite+Concierge` },
    },
  ],
};

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const idealClients = [
  'Weddings with structured budgets ($40K+)',
  'Corporate events with multiple stakeholders',
  'Clients who want to avoid costly mistakes',
  'Individuals who value expert guidance over guesswork',
];

const packages = [
  {
    name: 'Strategy Session',
    price: '$300',
    priceNote: '(applied toward any package)',
    description: 'A deep-dive consultation designed to give you immediate clarity and direction.',
    features: [
      'Budget insight and allocation strategy',
      'Vendor direction based on your event goals',
      'Early-stage risk identification',
    ],
    cta: 'Book Your Session',
    href: '/contact?services=Strategy+Session+%28%24300%29',
    accent: 'standard',
  },
  {
    name: 'Concierge Advisory',
    price: '$1,800',
    priceNote: 'Founders pricing · applied for early clients',
    description: 'Structured support for clients who want expert guidance without full planning.',
    features: [
      'Line-by-line budget architecture',
      'Vendor shortlist aligned to style and spend',
      'Contract and decision guidance',
      'Ongoing consultation as needed',
    ],
    cta: 'Request Details',
    href: '/contact?services=Concierge+Advisory',
    accent: 'featured',
  },
  {
    name: 'Full Concierge Oversight',
    price: '$3,500',
    priceNote: 'Founders pricing · applied for early clients',
    description: 'For clients who want consistent, high-level involvement throughout the process.',
    features: [
      'Vendor coordination and communication support',
      'Contract review and alignment guidance',
      'Budget monitoring and adjustments',
      'Continuous oversight from start to execution',
    ],
    cta: 'Inquire for Availability',
    href: '/contact?services=Full+Concierge+Oversight',
    accent: 'standard',
  },
  {
    name: 'Elite Concierge',
    price: 'Bespoke',
    priceNote: 'Fully tailored engagement',
    description: 'A fully tailored engagement for complex, high-investment events. Custom scope based on event scale, stakeholder involvement, and the level of discretion and detail required.',
    features: [
      'Event scale customization',
      'Multi-stakeholder coordination',
      'Highest level of discretion and detail',
    ],
    cta: 'Schedule a Private Consultation',
    href: '/contact?services=Elite+Concierge',
    accent: 'bespoke',
  },
];

export const Home = () => {
  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO
        title="White Glove Event Concierge | Nashville Event Advisory"
        description="Strategic event consulting for high-value events in Nashville. Budget strategy, vendor alignment, and contract guidance from $300."
        jsonLd={HOME_JSONLD}
      />
      {/* HERO */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-28 md:pt-32 pb-20" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="/assets/hero.jpg"
            alt="White Glove Event Concierge"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/65 to-[#050505]" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center">
          {/* Refined gold ornament divider */}
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex items-center justify-center gap-3 mb-8"
            aria-hidden="true"
          >
            <span className="h-px w-12 bg-gradient-to-r from-transparent to-[#D4AF37]" />
            <span className="w-1.5 h-1.5 rotate-45 bg-[#D4AF37]" />
            <span className="h-px w-12 bg-gradient-to-l from-transparent to-[#D4AF37]" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white mb-10 leading-[1.05] tracking-tight"
          >
            Nashville&apos;s Strategic<br />
            Event Concierge<br />
            <span className="text-gold-gradient italic">for High-Value Events</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto mb-5 leading-relaxed font-light"
          >
            We don&apos;t plan events. We ensure they&apos;re executed correctly — through budget strategy, vendor alignment, and oversight.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="text-white/55 text-sm md:text-base max-w-xl mx-auto mb-12 leading-relaxed"
          >
            From weddings to corporate events, we guide every decision so your investment performs the way it should.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link to="/contact?services=Strategy+Session+%28%24300%29" className="btn-primary" data-testid="hero-cta-primary">
              Start Your Event Strategy
            </Link>
            <Link to="/consulting" className="btn-secondary" data-testid="hero-cta-secondary">
              Explore Services
            </Link>
          </motion.div>

          {/* Trust strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-16 flex flex-wrap items-center justify-center gap-x-8 md:gap-x-10 gap-y-3 text-white/55"
            data-testid="hero-trust-strip"
          >
            <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em]">
              <span className="text-[#D4AF37] font-heading text-base">20+</span> Years Experience
            </span>
            <span className="hidden sm:inline w-1 h-1 bg-white/20 rounded-full" aria-hidden="true" />
            <span className="text-[11px] uppercase tracking-[0.3em]">Nashville-Based</span>
            <span className="hidden sm:inline w-1 h-1 bg-white/20 rounded-full" aria-hidden="true" />
            <span className="text-[11px] uppercase tracking-[0.3em]">Founders Pricing</span>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border border-white/30 rounded-full flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* POSITIONING — Clarity Before Commitment */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="positioning-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center">
            <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em]">Our Approach</span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mt-4 mb-10">
              Clarity Before <em className="text-gold-gradient">Commitment</em>
            </h2>
            <p className="text-white/75 text-lg leading-relaxed mb-10">
              Most event challenges don&apos;t come from execution — they come from early decisions made without the right structure.
            </p>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              We step in at the point where it matters most:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              'Before contracts are signed',
              'Before budgets are locked',
              'Before vendors are finalized',
            ].map((line, i) => (
              <motion.div
                key={line}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="p-6 bg-[#0a0a0a] border border-white/10 hover:border-[#D4AF37]/40 transition-colors text-center"
              >
                <p className="font-heading text-white text-lg leading-snug">{line}</p>
              </motion.div>
            ))}
          </div>

          <motion.p {...fadeInUp} className="text-white/65 text-base md:text-lg leading-relaxed text-center max-w-3xl mx-auto">
            Our role is to bring structure, alignment, and oversight so every moving part works together — not against you. We stay involved to ensure decisions remain aligned and nothing falls through the cracks.
          </motion.p>
        </div>
      </section>

      {/* SERVICES / PACKAGES */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-[#0a0a0a]" data-testid="services-section">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-[#D4AF37]/[0.05] blur-3xl rounded-full" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em]">Service Packages</span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mt-4 mb-4">
              Designed for Control, Clarity, and <em className="text-gold-gradient">Confidence</em>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {packages.map((pkg, index) => {
              const featured = pkg.accent === 'featured';
              const bespoke = pkg.accent === 'bespoke';
              return (
                <motion.div
                  key={pkg.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className={`relative p-8 backdrop-blur-sm transition-all flex flex-col ${
                    featured
                      ? 'bg-gradient-to-br from-[#141006] to-[#0d0a04] border border-[#D4AF37] shadow-[0_0_40px_rgba(212,175,55,0.12)]'
                      : bespoke
                      ? 'bg-gradient-to-br from-[#0a0d0e] to-[#080a0b] border border-[#D4AF37]/40'
                      : 'bg-gradient-to-br from-[#0d0b08] to-[#0a0a0a] border border-white/10 hover:border-white/30'
                  }`}
                  data-testid={`home-package-${index}`}
                >
                  {featured && (
                    <span className="absolute -top-3 left-8 px-3 py-1 bg-[#D4AF37] text-black text-[10px] uppercase tracking-[0.2em] font-semibold">
                      Most Requested
                    </span>
                  )}
                  {bespoke && (
                    <span className="absolute -top-3 left-8 px-3 py-1 bg-[#0a0a0a] border border-[#D4AF37] text-[#D4AF37] text-[10px] uppercase tracking-[0.2em]">
                      Bespoke
                    </span>
                  )}
                  <h3 className="font-heading text-2xl text-white mb-3">{pkg.name}</h3>
                  <div className="mb-5">
                    <p className="font-heading text-3xl text-[#D4AF37] leading-none">{pkg.price}</p>
                    {pkg.priceNote && (
                      <p className="text-white/50 text-xs italic mt-2">{pkg.priceNote}</p>
                    )}
                  </div>
                  <p className="text-white/75 text-sm leading-relaxed mb-6">{pkg.description}</p>
                  <ul className="space-y-3 mb-8 flex-1">
                    {pkg.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-white/70 text-sm">
                        <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={pkg.href}
                    className={`block text-center py-3 text-sm uppercase tracking-widest transition-colors ${
                      featured
                        ? 'bg-[#D4AF37] text-black hover:bg-[#C9A432]'
                        : bespoke
                        ? 'border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black'
                        : 'border border-white/20 text-white hover:bg-white/5'
                    }`}
                    data-testid={`home-package-cta-${index}`}
                  >
                    {pkg.cta}
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <motion.div {...fadeInUp} className="text-center mt-14">
            <Link
              to="/consulting"
              className="inline-flex items-center gap-2 px-8 py-3 border border-white/20 text-white text-sm uppercase tracking-widest hover:bg-white/5 transition-colors"
              data-testid="home-view-all-packages"
            >
              View Full Package Details <ArrowRight size={14} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="who-its-for-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-14">
            <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em]">Who This Is For</span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mt-4">
              Built for Clients Who Value <em className="text-gold-gradient">Precision</em>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-5">
            {idealClients.map((client, i) => (
              <motion.div
                key={client}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="flex items-start gap-4 p-6 bg-[#0a0a0a] border border-white/10 hover:border-[#D4AF37]/40 transition-colors"
                data-testid={`ideal-client-${i}`}
              >
                <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <p className="text-white/85 text-base md:text-lg leading-relaxed">{client}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-24 md:py-32 bg-[#0a0a0a]" data-testid="trust-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center">
            <ShieldCheck className="w-12 h-12 text-[#D4AF37] mx-auto mb-6" />
            <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em]">Experience</span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mt-4 mb-10">
              Confidence in Every <em className="text-gold-gradient">Decision</em>
            </h2>
            <p className="text-white/75 text-lg leading-relaxed mb-6">
              With over 20 years of experience in venues, catering, and event operations, we understand where events succeed — and where they fail.
            </p>
            <p className="text-white/85 text-lg md:text-xl font-heading italic">
              Our role is to ensure your event is built on decisions that hold up under pressure.
            </p>
          </motion.div>
        </div>
      </section>

      {/* NASHVILLE POSITIONING */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="nashville-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center">
            <MapPin className="w-10 h-10 text-[#D4AF37] mx-auto mb-6" />
            <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em]">Nashville</span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mt-4 mb-10">
              Rooted in Nashville&apos;s <em className="text-gold-gradient">Event Ecosystem</em>
            </h2>
            <p className="text-white/75 text-lg leading-relaxed">
              We work within Nashville&apos;s network of venues, vendors, and professionals to ensure every recommendation is grounded in real-world performance — not assumptions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA — Start With Strategy */}
      <section className="py-24 md:py-32 bg-[#0a0a0a]" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em]">Begin Here</span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mt-4 mb-6">
              Start With <em className="text-gold-gradient">Strategy</em>
            </h2>
            <p className="text-white/75 text-lg leading-relaxed mb-12 max-w-2xl mx-auto">
              Before committing to vendors, contracts, or major decisions, start with a clear plan.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?services=Strategy+Session+%28%24300%29" className="btn-primary" data-testid="final-cta-primary">
                Book Your Strategy Session
              </Link>
              <Link to="/contact" className="btn-secondary" data-testid="final-cta-secondary">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
